import { resolveSystemOverviewBody, systemContentKey, type DtoolsSystemContent } from "./dtoolsProposalContent";
import { filterVisibleDtoolsSystems, isCustomerFacingDtoolsSystem } from "./dtoolsOptionVisibility";
import {
  buildDtoolsReviewProposalTotals,
  buildDtoolsReviewSystemView,
  productPrimaryBlocks,
  resolveSystemDisplayLabel,
  type DtoolsReviewBuildContext,
} from "./dtoolsReviewNavigation";
import {
  shouldShowProposalSubtotalRow,
  type DtoolsDisplaySettings,
  type DtoolsProposalSummaryRow,
} from "./dtoolsDisplaySettings";
import type { DtoolsReviewFooter, DtoolsReviewSystem } from "./dtoolsStagingTransform";
import type { DtoolsV2CustomerProposalView } from "./dtoolsV2CustomerProposalView";
import { buildDtoolsViewByOption } from "./dtoolsCustomerProposalView";
import {
  collectGlobalBillableServiceSystems,
  collectInternalSystemCustomerSections,
  collectUnassignedOtherBillableSystems,
  DTOOLS_UNASSIGNED_OTHER_ITEMS_LABEL,
  sumInternalSystemCustomerSections,
} from "./dtoolsCustomerFacingDealTotal";
import { sumCustomerLineTotalsInHierarchySystem } from "./dtoolsReviewNavigation";

export type InternalSystemSummaryRow = {
  key: "global-services" | "global-other-items";
  label: string;
  description: string;
  amount: number;
};

/**
 * Customer summary rows for priced lines parked in internal placeholder systems:
 * "Services" (project-wide services) and "Other items" (products/cables/accessories).
 * Same helper as the Grand Total, Revise and the PDF — rows only appear when > 0.
 */
export function buildInternalSystemSummaryRows(
  systems: DtoolsReviewSystem[] | null | undefined,
  displaySettings?: DtoolsDisplaySettings,
): InternalSystemSummaryRow[] {
  if (!systems?.length) return [];
  const sum = (list: DtoolsReviewSystem[]) =>
    Math.round(list.reduce((acc, system) => acc + sumCustomerLineTotalsInHierarchySystem(system), 0) * 100) / 100;
  const rows: InternalSystemSummaryRow[] = [];
  const services = sum(collectGlobalBillableServiceSystems(systems, displaySettings));
  if (services > 0) {
    rows.push({
      key: "global-services",
      label: "Services",
      description: "Professional services included in this proposal.",
      amount: services,
    });
  }
  const other = sum(collectUnassignedOtherBillableSystems(systems, displaySettings));
  if (other > 0) {
    rows.push({
      key: "global-other-items",
      label: DTOOLS_UNASSIGNED_OTHER_ITEMS_LABEL,
      description: "Additional equipment and materials included in this proposal.",
      amount: other,
    });
  }
  return rows;
}

export type ProposalSnapshotMetrics = {
  systemsIncluded: number;
  roomsCovered: number;
  optionalSystems: number;
  proposalTotal: string;
};

export type SystemIncludedRow = {
  key: string;
  system: string;
  description: string;
  amount?: string;
};

export type ProposalAmountRow = {
  key: string;
  system: string;
  amount: string;
  muted?: boolean;
};

export type SystemSummaryTabContent = {
  snapshot: ProposalSnapshotMetrics;
  systemsIncludedRows: SystemIncludedRow[];
  proposalAmountRows: ProposalAmountRow[];
  proposalFooterRows: DtoolsProposalSummaryRow[];
};

const SUMMARY_MAX_CHARS = 360;
const SUMMARY_SENTENCE_LIMIT = 2;

function isLowInformationHeading(text: string): boolean {
  const compact = text.trim();
  if (!compact) return false;
  if (compact.length > 90) return false;
  return /^(option|system)\s*\d+\s*[-:–—]\s*[a-z0-9].*$/i.test(compact);
}

function hasBalancedBrackets(text: string): boolean {
  const stack: string[] = [];
  const pairs: Record<string, string> = { "(": ")", "[": "]", "{": "}" };
  const closing = new Set(Object.values(pairs));
  for (const char of text) {
    if (pairs[char]) {
      stack.push(pairs[char]);
      continue;
    }
    if (closing.has(char)) {
      const expected = stack.pop();
      if (!expected || expected !== char) return false;
    }
  }
  return stack.length === 0;
}

function trimToBalancedWordBoundary(text: string, maxLength: number): string {
  if (text.length <= maxLength && hasBalancedBrackets(text)) return text;
  let stack: string[] = [];
  const pairs: Record<string, string> = { "(": ")", "[": "]", "{": "}" };
  const closing = new Set(Object.values(pairs));
  let lastSafe = -1;
  const limit = Math.min(text.length, maxLength);
  for (let i = 0; i < limit; i += 1) {
    const char = text[i]!;
    if (pairs[char]) {
      stack.push(pairs[char]);
    } else if (closing.has(char)) {
      const expected = stack[stack.length - 1];
      if (expected === char) stack.pop();
      else break;
    }
    const boundary = /\s/.test(char) || /[.,;:!?)]/.test(char);
    if (boundary && stack.length === 0) lastSafe = i + 1;
  }
  const cutAt = lastSafe > 0 ? lastSafe : text.lastIndexOf(" ", maxLength);
  const truncated = text.slice(0, cutAt > 0 ? cutAt : maxLength).trim();
  return `${truncated.replace(/[.,;:!?-]+$/, "").trim()}…`;
}

function splitIntoSentences(text: string): string[] {
  const out: string[] = [];
  let start = 0;
  let stack: string[] = [];
  const pairs: Record<string, string> = { "(": ")", "[": "]", "{": "}" };
  const closing = new Set(Object.values(pairs));
  for (let i = 0; i < text.length; i += 1) {
    const char = text[i]!;
    if (pairs[char]) stack.push(pairs[char]);
    else if (closing.has(char)) {
      if (stack[stack.length - 1] === char) stack.pop();
    }
    const prev = text[i - 1] ?? "";
    const next = text[i + 1] ?? "";
    if (char === "." && /\d/.test(prev) && /\d/.test(next)) continue;
    if (!/[.!?]/.test(char) || stack.length > 0) continue;
    let end = i + 1;
    while (end < text.length && /["')\]]/.test(text[end]!)) end += 1;
    const sentence = text.slice(start, end).trim();
    if (sentence) out.push(sentence);
    start = end;
    while (start < text.length && /\s/.test(text[start]!)) start += 1;
  }
  const trailing = text.slice(start).trim();
  if (trailing) out.push(trailing);
  return out;
}

function summarizeSystemOverviewForSummary(content: DtoolsSystemContent | undefined): string {
  const body = resolveSystemOverviewBody(content)?.trim();
  if (!body) return "Included in this proposal.";
  const singleLine = body.replace(/\s+/g, " ").trim();
  const sentences = splitIntoSentences(singleLine);
  if (sentences.length === 0) return trimToBalancedWordBoundary(singleLine, SUMMARY_MAX_CHARS);
  const startsWithLowInformationHeading =
    isLowInformationHeading(sentences[0] ?? "") && sentences.length > 1;
  if (!startsWithLowInformationHeading && singleLine.length <= SUMMARY_MAX_CHARS && hasBalancedBrackets(singleLine)) {
    return singleLine;
  }

  const startIndex = startsWithLowInformationHeading ? 1 : 0;
  const picked: string[] = [];
  for (let i = startIndex; i < sentences.length; i += 1) {
    const sentence = sentences[i]!;
    const next = picked.length === 0 ? sentence : `${picked.join(" ")} ${sentence}`;
    if (picked.length > 0 && next.length > SUMMARY_MAX_CHARS) break;
    picked.push(sentence);
    if (picked.length >= SUMMARY_SENTENCE_LIMIT) break;
  }
  const candidate = picked.join(" ").trim();
  if (!candidate) return trimToBalancedWordBoundary(singleLine, SUMMARY_MAX_CHARS);
  if (candidate.length <= SUMMARY_MAX_CHARS && hasBalancedBrackets(candidate)) return candidate;
  return trimToBalancedWordBoundary(candidate, SUMMARY_MAX_CHARS);
}

function isSummaryEligibleSystem(
  system: DtoolsReviewSystem,
  displaySettings?: DtoolsDisplaySettings,
  audience: "customer" | "staff" = "customer",
): boolean {
  return isCustomerFacingDtoolsSystem(system, displaySettings, audience);
}

function normalizeRoomName(value: string | null | undefined): string {
  return String(value ?? "").trim() || "Unassigned";
}

/** Unique customer-visible rooms with product equipment across the included systems. */
export function countRoomsCoveredAcrossSystems(systems: DtoolsReviewSystem[]): number {
  const roomNames = new Set<string>();
  for (const system of systems) {
    for (const room of system.rooms) {
      if (productPrimaryBlocks(room.primaries).length > 0) {
        roomNames.add(normalizeRoomName(room.name));
      }
    }
  }
  return roomNames.size;
}

function buildProposalFooterRows(input: {
  totals: ReturnType<typeof buildDtoolsReviewProposalTotals>;
  formatMoney: (amount: number) => string;
  includeSystemsInAmountTable: boolean;
}): DtoolsProposalSummaryRow[] {
  const rows: DtoolsProposalSummaryRow[] = [];
  if (!input.includeSystemsInAmountTable && shouldShowProposalSubtotalRow(input.totals)) {
    rows.push({
      key: "systems-total",
      label: "Systems total",
      amount: input.formatMoney(input.totals.subtotal),
      strong: true,
      dividerBefore: true,
    });
  }
  if (input.totals.adjustmentsTotal !== 0) {
    rows.push({
      key: "adjustments",
      label: "Adjustments",
      amount: input.formatMoney(input.totals.adjustmentsTotal),
      dividerBefore: rows.length === 0,
    });
  }
  if (input.totals.tax != null && input.totals.tax > 0) {
    rows.push({
      key: "tax",
      label: "GST / tax",
      amount: input.formatMoney(input.totals.tax),
      dividerBefore: rows.length === 0,
    });
  }
  rows.push({
    key: "final",
    label: "Proposal total",
    amount: input.formatMoney(input.totals.finalTotal),
    strong: true,
    dividerBefore: true,
  });
  return rows;
}

function collectVisibleSystemsFromView(view: DtoolsV2CustomerProposalView): DtoolsReviewSystem[] {
  return [
    ...view.independentSections.map((section) => section.system),
    ...view.alternativeGroups.flatMap((group) => group.options.map((opt) => opt.system)),
  ];
}

/** Numeric hierarchy proposal total for customer pricing resolver fallbacks. */
export function resolveDtoolsHierarchyProposalTotalNumeric(input: {
  systems: DtoolsReviewSystem[];
  footerAdjustments: DtoolsReviewFooter["adjustments"];
  reconciliation?: Parameters<typeof buildDtoolsReviewProposalTotals>[0]["reconciliation"];
  displaySettings?: DtoolsDisplaySettings;
}): number | null {
  const visibleSystems = filterVisibleDtoolsSystems(input.systems, input.displaySettings, {
    audience: "customer",
  }).map((entry) => entry.system);
  const preferHeaderTotal =
    input.reconciliation?.headerTotal != null && Number.isFinite(Number(input.reconciliation.headerTotal));
  const totals = buildDtoolsReviewProposalTotals({
    systems: visibleSystems,
    footerAdjustments: input.footerAdjustments,
    reconciliation: input.reconciliation ?? undefined,
    preferHeaderTotal,
    projectTotal: preferHeaderTotal ? Number(input.reconciliation!.headerTotal) : null,
  });
  const selectedScopeTotal =
    totals.finalTotal + sumInternalSystemCustomerSections(input.systems, input.displaySettings);
  return selectedScopeTotal > 0 ? selectedScopeTotal : null;
}

export function buildV2SystemSummaryTabContent(input: {
  view: DtoolsV2CustomerProposalView;
  /** Full hierarchy (incl. internal placeholder systems) so priced Unassigned lines are summarised. */
  systems?: DtoolsReviewSystem[];
  footerAdjustments: DtoolsReviewFooter["adjustments"];
  displaySettings?: DtoolsDisplaySettings;
  reviewContext?: DtoolsReviewBuildContext;
  formatMoney: (amount: number) => string;
}): SystemSummaryTabContent {
  const displaySettings = input.displaySettings ?? {};
  const alternativeOptions = displaySettings.alternativeOptions === true;
  const systemContentMap = input.reviewContext?.systemContentMap ?? {};

  const systemsIncludedRows: SystemIncludedRow[] = [];
  const proposalAmountRows: ProposalAmountRow[] = [];

  for (const section of input.view.independentSections) {
    if (!isSummaryEligibleSystem(section.system, displaySettings, "customer")) continue;
    const systemView = buildDtoolsReviewSystemView(section.system, input.reviewContext, section.hierarchyIndex);
    systemsIncludedRows.push({
      key: `purpose-${section.sectionKey}`,
      system: section.title,
      description: summarizeSystemOverviewForSummary(systemContentMap[section.sectionKey]),
      amount: input.formatMoney(systemView.overview.systemTotal),
    });
    proposalAmountRows.push({
      key: `amount-${section.sectionKey}`,
      system: section.title,
      amount: input.formatMoney(systemView.overview.systemTotal),
    });
  }

  let optionalSystems = 0;
  for (const group of input.view.alternativeGroups) {
    optionalSystems += 1;
    const first = group.options[0];
    if (!first) continue;
    if (group.options.length === 1) {
      if (!isSummaryEligibleSystem(first.system, displaySettings, "customer")) continue;
      const systemView = buildDtoolsReviewSystemView(first.system, input.reviewContext, first.hierarchyIndex);
      systemsIncludedRows.push({
        key: `purpose-group-${group.groupKey}`,
        system: group.groupName,
        description: summarizeSystemOverviewForSummary(systemContentMap[first.optionKey]),
        amount: input.formatMoney(systemView.overview.systemTotal),
      });
      proposalAmountRows.push({
        key: `amount-group-${group.groupKey}`,
        system: group.groupName,
        amount: input.formatMoney(systemView.overview.systemTotal),
      });
      continue;
    }

    systemsIncludedRows.push({
      key: `purpose-group-${group.groupKey}`,
      system: group.groupName,
      description: `Alternative options — choose one of ${group.options.length} packages.`,
    });
    for (const opt of group.options) {
      const systemView = buildDtoolsReviewSystemView(opt.system, input.reviewContext, opt.hierarchyIndex);
      const label = resolveSystemDisplayLabel(opt.system, input.reviewContext, opt.hierarchyIndex);
      proposalAmountRows.push({
        key: `amount-group-${group.groupKey}-${opt.optionKey}`,
        system: label,
        amount: input.formatMoney(systemView.overview.systemTotal),
        muted: true,
      });
    }
  }

  const independentSystems = input.view.independentSections
    .map((section) => section.system)
    .filter((system) => isSummaryEligibleSystem(system, displaySettings, "customer"));
  const alternativeSystems = input.view.alternativeGroups.flatMap((group) =>
    group.options
      .map((opt) => opt.system)
      .filter((system) => isSummaryEligibleSystem(system, displaySettings, "customer")),
  );
  const allVisibleSystems = [...independentSystems, ...alternativeSystems];
  const internalSections = input.systems
    ? collectInternalSystemCustomerSections(input.systems, displaySettings)
    : [];
  for (const row of buildInternalSystemSummaryRows(input.systems, displaySettings)) {
    systemsIncludedRows.push({
      key: `purpose-${row.key}`,
      system: row.label,
      description: row.description,
      amount: input.formatMoney(row.amount),
    });
    proposalAmountRows.push({
      key: `amount-${row.key}`,
      system: row.label,
      amount: input.formatMoney(row.amount),
    });
  }
  const totals = buildDtoolsReviewProposalTotals({
    systems: [...(alternativeOptions ? independentSystems : allVisibleSystems), ...internalSections],
    footerAdjustments: input.footerAdjustments,
  });

  const roomsCovered = countRoomsCoveredAcrossSystems(allVisibleSystems);
  const systemsIncluded =
    input.view.independentSections.filter((section) =>
      isSummaryEligibleSystem(section.system, displaySettings, "customer"),
    ).length +
    input.view.alternativeGroups.filter((group) => {
      const first = group.options[0];
      return first ? isSummaryEligibleSystem(first.system, displaySettings, "customer") : false;
    }).length;

  return {
    snapshot: {
      systemsIncluded,
      roomsCovered,
      optionalSystems,
      proposalTotal: input.formatMoney(totals.finalTotal),
    },
    systemsIncludedRows,
    proposalAmountRows,
    proposalFooterRows: buildProposalFooterRows({
      totals,
      formatMoney: input.formatMoney,
      includeSystemsInAmountTable: proposalAmountRows.length > 0,
    }),
  };
}

export function buildV1SystemSummaryTabContent(input: {
  systems: DtoolsReviewSystem[];
  footerAdjustments: DtoolsReviewFooter["adjustments"];
  displaySettings?: DtoolsDisplaySettings;
  reviewContext?: DtoolsReviewBuildContext;
  formatMoney: (amount: number) => string;
}): SystemSummaryTabContent {
  const optionGroups = buildDtoolsViewByOption(input.systems, input.reviewContext, input.displaySettings);
  const systemContentMap = input.reviewContext?.systemContentMap ?? {};
  const systemsIncludedRows: SystemIncludedRow[] = [];
  const proposalAmountRows: ProposalAmountRow[] = [];

  for (const group of optionGroups) {
    if (!isSummaryEligibleSystem(group.system, input.displaySettings, "customer")) continue;
    const key = systemContentKey(group.system.systemId, group.system.name) || `option-${group.optionIndex}`;
    const systemView = buildDtoolsReviewSystemView(group.system, input.reviewContext, group.optionIndex);
    systemsIncludedRows.push({
      key: `purpose-${key}`,
      system: group.optionLabel,
      description: summarizeSystemOverviewForSummary(systemContentMap[key]),
      amount: input.formatMoney(systemView.overview.systemTotal),
    });
    proposalAmountRows.push({
      key: `amount-${key}`,
      system: group.optionLabel,
      amount: input.formatMoney(systemView.overview.systemTotal),
    });
  }

  const internalSections = collectInternalSystemCustomerSections(input.systems, input.displaySettings);
  for (const row of buildInternalSystemSummaryRows(input.systems, input.displaySettings)) {
    systemsIncludedRows.push({
      key: `purpose-${row.key}`,
      system: row.label,
      description: row.description,
      amount: input.formatMoney(row.amount),
    });
    proposalAmountRows.push({
      key: `amount-${row.key}`,
      system: row.label,
      amount: input.formatMoney(row.amount),
    });
  }

  const visibleSystems = optionGroups
    .map((group) => group.system)
    .filter((system) => isSummaryEligibleSystem(system, input.displaySettings, "customer"));
  const totals = buildDtoolsReviewProposalTotals({
    systems: [...visibleSystems, ...internalSections],
    footerAdjustments: input.footerAdjustments,
  });
  const roomsCovered = countRoomsCoveredAcrossSystems(visibleSystems);
  const eligibleGroups = optionGroups.filter((group) =>
    isSummaryEligibleSystem(group.system, input.displaySettings, "customer"),
  );
  const optionalSystems = eligibleGroups.length > 1 ? 1 : 0;

  return {
    snapshot: {
      systemsIncluded: eligibleGroups.length,
      roomsCovered,
      optionalSystems,
      proposalTotal: input.formatMoney(totals.finalTotal),
    },
    systemsIncludedRows,
    proposalAmountRows,
    proposalFooterRows: buildProposalFooterRows({
      totals,
      formatMoney: input.formatMoney,
      includeSystemsInAmountTable: proposalAmountRows.length > 0,
    }),
  };
}

/** @deprecated Use buildV2SystemSummaryTabContent */
export function buildV2SystemSummaryContent(input: {
  view: DtoolsV2CustomerProposalView;
  footerAdjustments: DtoolsReviewFooter["adjustments"];
  displaySettings?: DtoolsDisplaySettings;
  reviewContext?: DtoolsReviewBuildContext;
  formatMoney: (amount: number) => string;
  audience?: "customer" | "staff";
}) {
  const content = buildV2SystemSummaryTabContent(input);
  return {
    tableRows: content.systemsIncludedRows.map((row) => ({
      key: row.key,
      system: row.system,
      description: row.description,
      value: "",
    })),
    pricingRows: content.proposalFooterRows,
  };
}

export { collectVisibleSystemsFromView, summarizeSystemOverviewForSummary };
